import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import usePermissions from '../auth/usePermissions';
import usePlantScope from '../auth/usePlantScope';
import useIsMobile from '../auth/useIsMobile';
import {
  archivePart,
  deletePart,
  getPartByCode,
  restorePart,
  updatePart,
} from '../services/parts';
import { getSupplierByCode, listSuppliers } from '../services/suppliers';
import { listPlants } from '../services/plants';
// Coverage events used to be loaded and mutated from this page — that
// tracking has moved to the Suppliers page in the Sales dashboard.
import PartForm from './PartForm';

/**
 * Part detail page — shows everything about a single part, with links out
 * to its supplier and each plant it ships to. Edit reopens the same form
 * used elsewhere.
 */
export default function PartDetail() {
  const { partCode } = useParams();
  const { canEditParts, isLiaisonManager } = usePermissions();
  // Liaison Managers only see parts used at their assigned plants (per
  // Jillian, July 2026) — guard below covers direct links.
  const {
    allows: allowsPlant,
    allowsSupplier,
    loading: scopeLoading,
  } = usePlantScope();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [part, setPart] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [plants, setPlants] = useState([]);
  // Full suppliers list — feeds the Supplier dropdown when the Director
  // opens the edit form and wants to re-assign the part.
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [archivingState, setArchivingState] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await getPartByCode(partCode);
      setPart(p);
      if (p?.SupplierCode) {
        try {
          const s = await getSupplierByCode(p.SupplierCode);
          setSupplier(s);
        } catch {
          setSupplier(null);
        }
      } else {
        setSupplier(null);
      }
      // Plants list powers the edit form's plant+vehicle picker and lets us
      // resolve plant codes to human names on this detail page.
      try {
        const plantRows = await listPlants();
        setPlants(plantRows || []);
      } catch {
        setPlants([]);
      }
      // Full suppliers list for the edit form's Supplier dropdown. Failures
      // here are non-fatal — the form falls back to just the current
      // supplier code so at least the existing value stays visible.
      try {
        const supRows = await listSuppliers();
        setSuppliers(supRows || []);
      } catch {
        setSuppliers([]);
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [partCode]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (model) => {
    setSaving(true);
    try {
      await updatePart(part.id, model);
      setEditing(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  // Delete a part. Plain window.confirm matches the pattern used for
  // Agreement / Sales Resource deletes — a dedicated confirm modal is
  // overkill for a deliberate click. Reachable by everyone: the header
  // "Remove" button (all roles) and the edit-modal footer both call this.
  // After a successful delete we navigate back to the Parts index since
  // the current detail route would 404 on refresh.
  const onDelete = async () => {
    if (!part?.id) return;
    const label = part.Title ? `the part "${part.Title}"` : 'this part';
    if (!window.confirm(`Delete ${label}? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await deletePart(part.id);
      // Coverage-event purge used to fire here when a part was deleted —
      // coverage tracking has moved to the Supplier side of the Sales
      // dashboard, so no per-part cleanup is needed anymore.
      navigate('/parts');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[PartDetail] Delete failed:', err);
      window.alert(`Couldn't delete the part: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Archive (deactivate) or restore a part. Soft-delete: the record stays,
  // but archived parts drop out of the active Parts list and can be brought
  // back. Available to everyone (per Jillian, Aug 2026), same as Remove.
  // Archiving is reversible, so it gets a lighter confirm than delete.
  const onArchive = async () => {
    if (!part?.id) return;
    const label = part.Title ? `"${part.Title}"` : 'this part';
    if (
      !window.confirm(
        `Archive ${label}? It will be moved out of the active Parts list ` +
          `but kept in history, and you can restore it anytime.`,
      )
    )
      return;
    setArchivingState(true);
    try {
      await archivePart(part.id);
      await load();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[PartDetail] Archive failed:', err);
      window.alert(`Couldn't archive the part: ${err.message}`);
    } finally {
      setArchivingState(false);
    }
  };

  const onRestore = async () => {
    if (!part?.id) return;
    setArchivingState(true);
    try {
      await restorePart(part.id);
      await load();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[PartDetail] Restore failed:', err);
      window.alert(`Couldn't restore the part: ${err.message}`);
    } finally {
      setArchivingState(false);
    }
  };

  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Couldn't Load This Part</div>
        <p>{error.message}</p>
        <button className="btn btn-outline btn-sm" onClick={load}>
          Retry
        </button>
      </div>
    );
  }
  if (part && isLiaisonManager) {
    // LM plant-scope guard — a part is visible only when at least one of
    // its plant+vehicle pairs sits at one of the LM's assigned plants.
    if (scopeLoading) return <Loading />;
    const anyAllowed =
      (part.plantVehicles || []).some((pv) => allowsPlant(pv.plantCode)) &&
      // Sep 2026: and the part's supplier must be one the LM is
      // responsible for.
      allowsSupplier(part.SupplierCode);
    if (!anyAllowed) {
      return (
        <div className="card">
          <div className="card-title">Part Not Available</div>
          <p className="muted">
            This part isn&apos;t used at any of your assigned plants.
          </p>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => navigate('/parts')}
          >
            Back to Parts
          </button>
        </div>
      );
    }
  }
  if (!part) {
    return (
      <div className="card">
        <div className="card-title">Part Not Found</div>
        <p>
          No part with code <strong>{partCode}</strong>.
        </p>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/parts')}>
          Back to Parts
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div style={{ fontSize: 12, color: 'var(--brand-muted)' }}>
            <Link to="/parts">Parts</Link>
          </div>
          <h1 style={{ margin: '4px 0 0' }}>{part.Title}</h1>
        </div>
        <div className="actions">
          <button className="btn btn-outline" onClick={() => navigate('/parts')}>
            ← Parts
          </button>
          {canEditParts && (
            <button className="btn btn-primary" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
          {/* Deactivate/Archive + Restore — available to EVERYONE (per
              Jillian, Aug 2026). Archiving is the preferred way to retire a
              part that's no longer shipped but should stay in history;
              Remove (delete) is for mistakes / duplicates. An archived part
              shows Restore instead of Archive. */}
          {part.archived ? (
            <button
              className="btn btn-outline"
              onClick={onRestore}
              disabled={archivingState || deleting}
            >
              {archivingState ? 'Restoring…' : 'Restore'}
            </button>
          ) : (
            <button
              className="btn btn-outline"
              onClick={onArchive}
              disabled={archivingState || deleting}
            >
              {archivingState ? 'Archiving…' : 'Deactivate / Archive'}
            </button>
          )}
          {/* Remove — available to EVERYONE (per Jillian, July 2026). Any
              signed-in team member can delete a part from here, regardless
              of whether they have edit rights, so it lives in the header
              rather than only inside the (edit-gated) modal. Same
              window.confirm guard as onDelete below. */}
          <button
            className="btn btn-outline"
            style={{ color: 'var(--brand-danger)', borderColor: 'var(--brand-danger)' }}
            onClick={onDelete}
            disabled={deleting || archivingState}
          >
            {deleting ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>

      {part.archived && (
        <div
          className="card"
          style={{
            borderColor: 'var(--brand-muted, #9ca3af)',
            background: 'var(--brand-surface-muted, #f9fafb)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ color: 'var(--brand-muted)', fontSize: 14 }}>
            <strong>This part is archived.</strong> It's kept for history but
            hidden from the active Parts list. Use <em>Restore</em> to
            reactivate it.
          </span>
          <button
            className="btn btn-outline btn-sm"
            onClick={onRestore}
            disabled={archivingState || deleting}
          >
            {archivingState ? 'Restoring…' : 'Restore'}
          </button>
        </div>
      )}

      <div className="card">
        <div className="card-title">Info</div>
        <DetailGrid>
          {/* Part Number + Description moved to the per-part cards in
              Part/s Info below (Jillian, Aug 2026), so this card carries
              just the supplier and the part's name. */}
          <Field
            label="Supplier"
            value={
              supplier ? (
                <Link to={`/suppliers/${encodeURIComponent(supplier.SupplierCode)}`}>
                  {supplier.Title}
                </Link>
              ) : (
                part.SupplierCode
              )
            }
          />
          <Field label="Part Name" value={part.Title} />
        </DetailGrid>
      </div>

      <div className="card">
        <div className="card-title">Plants & Vehicles Receiving This Part</div>
        {part.plantVehicles?.length ? (
          (() => {
            // Group plant-vehicle rows by plantCode so each plant shows up
            // once with its vehicles underneath. Makes a Ram 1500 + Ram HD
            // situation at SHAP read as "SHAP · Ram 1500, Ram HD" instead of
            // two independent blocks.
            const byPlant = {};
            for (const pv of part.plantVehicles) {
              const code = pv.plantCode;
              if (!code) continue;
              if (!byPlant[code]) byPlant[code] = [];
              if (pv.vehicle) byPlant[code].push(pv.vehicle);
            }
            const plantByCode = {};
            for (const p of plants) if (p.PlantCode) plantByCode[p.PlantCode] = p;
            const codes = Object.keys(byPlant);
            return (
              <table className="mini-table">
                <thead>
                  <tr>
                    <th>Plant</th>
                    <th>Vehicles</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((code) => {
                    const plant = plantByCode[code];
                    const vehicles = byPlant[code];
                    return (
                      <tr key={code}>
                        <td>
                          <Link to={`/plants/${encodeURIComponent(code)}`}>
                            {plant?.Title || code}
                          </Link>
                        </td>
                        <td>
                          {vehicles.length ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {vehicles.map((v) => (
                                <span key={v} className="badge vehicle">{v}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="muted">All vehicles</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()
        ) : (
          <p className="muted">Not linked to any plants yet.</p>
        )}
      </div>

      {(() => {
        // Plant-Side Locations now reads per-variant rows (matches the
        // form's new table). For legacy parts that haven't been
        // re-saved since the restructure, back-fill variant[0] from the
        // old flat AssemblyLocation / KittingStorageLocation /
        // WarehouseLocation columns and map `notes` onto `description`
        // — mirrors the same migration buildModel runs in PartForm so
        // the detail page and edit form stay in sync.
        const baseVariants = part.variants?.length ? part.variants : [];
        const hasLegacyFlat =
          part.AssemblyLocation ||
          part.KittingStorageLocation ||
          part.WarehouseLocation;
        const source =
          baseVariants.length === 0 && hasLegacyFlat ? [{}] : baseVariants;
        const displayVariants = source.map((v, i) => ({
          variantName: v.variantName || '',
          // Part #, Description, and Notes moved to the per-part cards.
          // For legacy parts not re-saved since the restructure, back-fill
          // card[0] from the old record-level PartNumber / Description /
          // Notes so nothing disappears from view — mirrors the same
          // migration buildModel runs in PartForm.
          description:
            (v.description != null ? v.description : v.notes || '') ||
            (i === 0 ? part.Description || '' : ''),
          partNumber: v.partNumber || (i === 0 ? part.PartNumber || '' : ''),
          notes: v.notes || (i === 0 ? part.Notes || '' : ''),
          sequencingLocation:
            v.sequencingLocation ||
            (i === 0 ? part.KittingStorageLocation || '' : ''),
          assemblyLocation:
            v.assemblyLocation || (i === 0 ? part.AssemblyLocation || '' : ''),
          warehouseLocation:
            v.warehouseLocation ||
            (i === 0 ? part.WarehouseLocation || '' : ''),
        }));
        const hasAny = displayVariants.some(
          (v) =>
            v.variantName ||
            v.description ||
            v.partNumber ||
            v.sequencingLocation ||
            v.assemblyLocation ||
            v.warehouseLocation ||
            v.notes,
        );
        return (
          <div className="card">
            <div className="card-title">Part/s Info</div>
            {hasAny ? (
              <div style={{ overflowX: 'auto' }}>
                <table className="mini-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Part Type</th>
                      <th>Part Description</th>
                      <th>Part #</th>
                      {!isMobile && (
                        <th>
                          Sequenced
                          <div className="muted tiny" style={{ fontWeight: 400 }}>
                            (Sequencing Location)
                          </div>
                        </th>
                      )}
                      <th>
                        Installed
                        <div className="muted tiny" style={{ fontWeight: 400 }}>
                          (Assembly Location)
                        </div>
                      </th>
                      <th>
                        Kitting / Storage
                        <div className="muted tiny" style={{ fontWeight: 400 }}>
                          (Warehouse Location)
                        </div>
                      </th>
                      {!isMobile && <th>Notes</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {displayVariants.map((v, i) => (
                      <tr key={i}>
                        <td>{v.variantName || '—'}</td>
                        <td>{v.description || '—'}</td>
                        <td>{v.partNumber || '—'}</td>
                        {!isMobile && <td>{v.sequencingLocation || '—'}</td>}
                        <td>{v.assemblyLocation || '—'}</td>
                        <td>{v.warehouseLocation || '—'}</td>
                        {!isMobile && (
                          <td style={{ whiteSpace: 'pre-wrap' }}>
                            {v.notes || '—'}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="muted">
                No part details recorded yet. Open Edit to add a part.
              </p>
            )}
          </div>
        );
      })()}

      {/* Record-level Notes card removed — Notes are now per-part and
          shown in the Part/s Info table above (Jillian, Aug 2026). Legacy
          record-level Notes back-fills onto the first row there. */}

      <Modal
        open={editing}
        onClose={() => (saving || deleting ? null : setEditing(false))}
        title={`Edit ${part.Title}`}
      >
        {editing && (
          <PartForm
            initial={part}
            plants={plants}
            suppliers={suppliers}
            saving={saving}
            deleting={deleting}
            onSave={onSave}
            onCancel={() => setEditing(false)}
            // Remove is open to everyone (per Jillian, July 2026), so the
            // in-modal delete is no longer Director-gated — anyone who can
            // open the edit form also gets the footer delete, matching the
            // header "Remove" button.
            onDelete={onDelete}
          />
        )}
      </Modal>
    </>
  );
}

function DetailGrid({ children }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, value, full }) {
  const display =
    value === null || value === undefined || value === '' ? (
      <span className="muted">—</span>
    ) : (
      value
    );
  return (
    <div style={full ? { gridColumn: '1 / -1' } : undefined}>
      <div style={{ fontSize: 12, color: 'var(--brand-muted)' }}>{label}</div>
      <div style={{ fontSize: 14 }}>{display}</div>
    </div>
  );
}

